import { type Dispatch, type RefObject, type SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { v4 as uuidv4 } from "uuid";

import { isRealUserMessage } from "@/lib/attachments/auto-chip-plans";
import type { Attachment, ChatMessage } from "@/types/chat-message";

/** Small pause that lets the aborted stream settle before a steered send. */
const STEER_HANDOFF_DELAY_MS = 150;

export interface ChatSubmitOptions {
  /** Hides internal trigger messages from the visible transcript. */
  hidden?: boolean;
  /** Marks a queued item promoted while interrupting the active response. */
  steered?: boolean;
}

export interface QueuedChatMessage {
  /** Stable client-side identifier for queue operations. */
  id: string;
  /** Text that will be sent when this queue item reaches the front. */
  message: string;
  /** Attachments captured at queue time. */
  attachments?: Attachment[];
  /** Whether this item interrupted an active response when promoted. */
  interruptedActiveStream?: boolean;
  /** Per-send options used by plan approval and hidden trigger messages. */
  options?: ChatSubmitOptions;
}

interface UseChatMessageQueueInput {
  /** True while the current chat request is submitted or streaming. */
  loading: boolean;
  /** Synchronous busy flag set before React has a chance to re-render. */
  activeRequestRef: RefObject<boolean>;
  /** AI SDK stop handler for the active request. */
  stop: () => void;
  /** React state setter from useChat, used to remove interrupted tails. */
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>;
  /** Live submit function from Chat. */
  submitChat: (
    message: string,
    attachments?: Attachment[],
    options?: ChatSubmitOptions,
  ) => Promise<void>;
  /** Updates the visible streaming status line. */
  setDataStatus: (status: string) => void;
}

/**
 * Removes the incomplete assistant/tool tail left by an interrupted stream.
 * Steer is a replacement instruction, so the next request should continue
 * from the last complete human turn rather than the half-written answer.
 */
function pruneInterruptedAssistantTail(messages: ChatMessage[]): ChatMessage[] {
  let lastRealUserIndex = -1;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (
      message.role === "user" &&
      isRealUserMessage(message) &&
      message.metadata?.hidden !== true
    ) {
      lastRealUserIndex = i;
      break;
    }
  }

  if (lastRealUserIndex === -1 || lastRealUserIndex === messages.length - 1) {
    return messages;
  }

  return messages.slice(0, lastRealUserIndex + 1);
}

/**
 * Owns queued message state and the steer handoff behavior for Chat.
 * Keeps interruption-specific logic out of the main chat rendering component.
 */
export function useChatMessageQueue({
  loading,
  activeRequestRef,
  stop,
  setMessages,
  submitChat,
  setDataStatus,
}: UseChatMessageQueueInput) {
  const [queuedMessages, setQueuedMessages] = useState<QueuedChatMessage[]>([]);
  const [steeredQueuedMessage, setSteeredQueuedMessage] = useState<QueuedChatMessage | null>(null);
  const [isStoppingStream, setIsStoppingStream] = useState(false);
  const drainingQueueRef = useRef(false);
  const submitChatRef = useRef(submitChat);

  useEffect(() => {
    submitChatRef.current = submitChat;
  });

  const shouldQueueMessage = useCallback(() => {
    return (loading || activeRequestRef.current) && !drainingQueueRef.current;
  }, [activeRequestRef, loading]);

  const queueMessage = useCallback((
    message: string,
    attachments?: Attachment[],
    options?: ChatSubmitOptions,
  ) => {
    setQueuedMessages((prev) => [
      ...prev,
      {
        id: uuidv4(),
        message: message.trim(),
        attachments: attachments ? [...attachments] : undefined,
        options,
      },
    ]);
  }, []);

  const removeQueuedMessage = useCallback((queuedMessageId: string) => {
    setQueuedMessages((prev) => prev.filter((item) => item.id !== queuedMessageId));
  }, []);

  const stopStreaming = useCallback(() => {
    if (!loading) {
      return;
    }

    setIsStoppingStream(true);
    setDataStatus("Stopping response...");
    stop();
  }, [loading, setDataStatus, stop]);

  const steerQueuedMessage = useCallback((queuedMessageId: string) => {
    const selectedMessage = queuedMessages.find((item) => item.id === queuedMessageId) ?? null;
    if (!selectedMessage) {
      return;
    }

    if (loading) {
      setSteeredQueuedMessage({ ...selectedMessage, interruptedActiveStream: true });
      stopStreaming();
    } else {
      setSteeredQueuedMessage(selectedMessage);
    }
    setQueuedMessages((prev) => prev.filter((item) => item.id !== queuedMessageId));
  }, [loading, queuedMessages, stopStreaming]);

  useEffect(() => {
    if (!loading) {
      setIsStoppingStream(false);
    }
  }, [loading]);

  useEffect(() => {
    if (loading || drainingQueueRef.current) {
      return;
    }

    const nextQueuedMessage = steeredQueuedMessage ?? queuedMessages[0];
    if (!nextQueuedMessage) {
      return;
    }

    const shouldSteer = steeredQueuedMessage !== null;
    const shouldPruneInterruptedTail = nextQueuedMessage.interruptedActiveStream === true;
    drainingQueueRef.current = true;

    const drainNextMessage = async () => {
      if (shouldSteer) {
        flushSync(() => {
          if (shouldPruneInterruptedTail) {
            setMessages((prev) => pruneInterruptedAssistantTail(prev));
          }
          setSteeredQueuedMessage(null);
        });
        if (shouldPruneInterruptedTail) {
          await new Promise((resolve) => window.setTimeout(resolve, STEER_HANDOFF_DELAY_MS));
        }
      } else {
        setQueuedMessages((prev) => prev.slice(1));
      }

      await submitChatRef.current(
        nextQueuedMessage.message,
        nextQueuedMessage.attachments,
        {
          ...nextQueuedMessage.options,
          ...(shouldSteer ? { steered: true } : {}),
        },
      );
    };

    void drainNextMessage().finally(() => {
      drainingQueueRef.current = false;
    });
  }, [loading, queuedMessages, setMessages, steeredQueuedMessage]);

  return {
    isStoppingStream,
    queuedMessages,
    queueMessage,
    removeQueuedMessage,
    shouldQueueMessage,
    steerQueuedMessage,
    stopStreaming,
  };
}
